/**
 * 確認画面で組み立てた1行分のプレビュー値を、実際の登録時に近い「型付け・グルーピング済み」形へ
 * 整形する純ロジック（#4728）。
 *
 * API呼び出しは行わない——マスタコード（荷主コード等）のID解決はAPIの責務
 * （#4071①の境界「形式チェックはFE完結、マスタ整合はAPI」を踏襲）。ここでは日付・数値の
 * 型変換とロジGo項目グループ（必須/配送情報/運賃・費用/明細項目/カスタムキー/配車済み情報）への
 * 整形のみを行い、「こんな感じで登録されます」を実データで見せる。
 */

import {
  LOGI_FIELD_GROUP_LABEL,
  LOGI_FIELD_GROUPS,
  LOGI_FIELDS,
  type LogiFieldGroup,
  type LogiFieldValue
} from './logi-fields'
import {
  DATE_FIELDS,
  MASTER_REFERENCE_FIELDS,
  NUMBER_FIELDS,
  type PreviewDataRow
} from './preview'

/** マスタ参照コード項目（登録時に API 側でコードから ID へ解決される）。 */
const MASTER_CODE_FIELDS: ReadonlySet<LogiFieldValue> = MASTER_REFERENCE_FIELDS

export type ShapedFieldKind = 'date' | 'number' | 'masterCode' | 'string'

export type ShapedField = {
  value: LogiFieldValue
  label: string
  kind: ShapedFieldKind
  /** CSVからの元の値（未加工、インライン修正・一括入力の反映後）。 */
  raw: string
  /** 型変換後の表示用値（date は yyyy-MM-dd[THH:mm[:ss]]、number はカンマ除去済み）。 */
  shaped: string
}

export type ShapedRegistrationGroup = {
  group: LogiFieldGroup
  groupLabel: string
  fields: ShapedField[]
}

/** yyyy/MM/dd・yyyyMMdd・yyyy-MM-dd（+任意の時刻）を yyyy-MM-dd[THH:mm[:ss]] に正規化する。 */
const shapeDate = (raw: string): string => {
  const v = raw.trim()
  const normalized = /^\d{8}$/.test(v)
    ? `${v.slice(0, 4)}/${v.slice(4, 6)}/${v.slice(6, 8)}`
    : v
  const [datePart, timePart] = normalized.split(/[ T]/)
  const isoDate = datePart.replace(/\//g, '-')
  return timePart ? `${isoDate}T${timePart}` : isoDate
}

/** カンマ区切りを除去した数値表記に正規化する。数値として読めない場合は元の値のまま返す。 */
const shapeNumber = (raw: string): string => {
  const v = raw.trim().replace(/,/g, '')
  const n = Number(v)
  return Number.isNaN(n) ? raw : String(n)
}

const kindOf = (value: LogiFieldValue): ShapedFieldKind => {
  if (DATE_FIELDS.has(value)) return 'date'
  if (NUMBER_FIELDS.has(value)) return 'number'
  if (MASTER_CODE_FIELDS.has(value)) return 'masterCode'
  return 'string'
}

const shapeValue = (kind: ShapedFieldKind, raw: string): string => {
  switch (kind) {
    case 'date':
      return shapeDate(raw)
    case 'number':
      return shapeNumber(raw)
    case 'masterCode':
    case 'string':
      return raw
  }
}

/**
 * 1行分のプレビュー値（cells）を、実登録時の項目グループごとに型変換して整形する。
 * 値が空の項目（未マッピング・後で手入力予定で未入力のまま）はそのグループから除外し、
 * 全項目が空だったグループ自体も出力しない。
 */
export const shapeRegistrationRow = (
  row: PreviewDataRow
): ShapedRegistrationGroup[] =>
  LOGI_FIELD_GROUPS.flatMap((group) => {
    const fields: ShapedField[] = []
    for (const f of LOGI_FIELDS) {
      if (f.group !== group) continue
      const raw = row.cells[f.value]
      if (raw === undefined || raw.trim() === '') continue
      const kind = kindOf(f.value)
      fields.push({
        value: f.value,
        label: f.label,
        kind,
        raw,
        shaped: shapeValue(kind, raw)
      })
    }
    if (fields.length === 0) return []
    return [{ group, groupLabel: LOGI_FIELD_GROUP_LABEL[group], fields }]
  })
